import React, { useState, useEffect } from "react";
import { db } from "../../../firebase";
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { 
  Modal, 
  Button, 
  Form, 
  Container, 
  Row, 
  Col, 
  Table, 
  Spinner,
  Card,
  Badge,
  InputGroup,
  FormControl,
  Tab,
  Tabs,
  Pagination,
  Dropdown
} from "react-bootstrap";
import { 
  FaCalendarAlt, 
  FaUserAlt, 
  FaCar, 
  FaEdit, 
  FaTrash, 
  FaSearch,
  FaExclamationTriangle,
  FaEye,
  FaEnvelope,
  FaPhone,
  FaTools,
  FaFileAlt,
  FaDownload,
  FaSyncAlt
} from "react-icons/fa";
import AdminHeader from "../../../Components/AdminHeader";
import Sidebar from "../../../Components/Sidebar";

const AppointmentsData = () => {
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentAppointmentId, setCurrentAppointmentId] = useState(null);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [state, setState] = useState("");
  const [variant, setVariant] = useState("");
  const [year, setYear] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState("table");
  const [activeTab, setActiveTab] = useState("personal");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [filterService, setFilterService] = useState("All");

  // Pagination settings
  const itemsPerPage = 10;
  const pageLimit = 5;

  useEffect(() => {
    fetchAppointments();
  }, []);

  useEffect(() => {
    // Filter appointments based on search term and service filter
    if (appointments.length > 0) {
      let filtered = [...appointments];
      
      // Apply service filter
      if (filterService !== "All") {
        filtered = filtered.filter(appointment => 
          appointment.services.selectedService === filterService
        );
      }
      
      // Apply search filter
      if (searchTerm) {
        filtered = filtered.filter(appointment => 
          (appointment.contact.fullName && appointment.contact.fullName.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (appointment.contact.email && appointment.contact.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (appointment.appointment.PhoneNumber && appointment.appointment.PhoneNumber.includes(searchTerm)) ||
          (appointment.vehicleDetails.registrationNumber && appointment.vehicleDetails.registrationNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (appointment.vehicleDetails.make && appointment.vehicleDetails.make.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (appointment.vehicleDetails.model && appointment.vehicleDetails.model.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }
      
      setFilteredAppointments(filtered);
      setCurrentPage(1);
    }
  }, [searchTerm, appointments, filterService]);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const appointmentsCollection = collection(db, "appointments");
      const snapshot = await getDocs(appointmentsCollection);
      const appointmentsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAppointments(appointmentsData);
      setFilteredAppointments(appointmentsData);
    } catch (error) {
      console.error("Error fetching appointments: ", error);
      toast.error("Failed to fetch appointments. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (appointmentId) => {
    const appointmentToEdit = appointments.find(
      (appointment) => appointment.id === appointmentId
    );
    if (appointmentToEdit) {
      setCurrentAppointmentId(appointmentId);
      setFullName(appointmentToEdit.contact.fullName || "");
      setEmail(appointmentToEdit.contact.email || "");
      setAppointmentDate(appointmentToEdit.appointment.appointmentDate || "");
      setAppointmentTime(appointmentToEdit.appointment.appointmentTime || "");
      setPhoneNumber(appointmentToEdit.appointment.PhoneNumber || "");
      setNotes(appointmentToEdit.contact.notes || "");
      setSelectedService(appointmentToEdit.services.selectedService || "");
      setMake(appointmentToEdit.vehicleDetails.make || "");
      setModel(appointmentToEdit.vehicleDetails.model || "");
      setRegistrationNumber(appointmentToEdit.vehicleDetails.registrationNumber || "");
      setState(appointmentToEdit.vehicleDetails.state || "");
      setVariant(appointmentToEdit.vehicleDetails.variant || "");
      setYear(appointmentToEdit.vehicleDetails.year || "");
      setShowModal(true);
      setActiveTab("personal");
    }
  };

  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setShowDetailsModal(true);
  };

  const handleDelete = async (appointmentId) => {
    if (window.confirm("Are you sure you want to delete this appointment?")) {
      setLoading(true);
      try {
        const appointmentRef = doc(db, "appointments", appointmentId);
        await deleteDoc(appointmentRef);
        toast.success("Appointment deleted successfully!");
        fetchAppointments(); // Refresh appointments after deletion
      } catch (error) {
        console.error("Error deleting appointment: ", error);
        toast.error("Failed to delete appointment. Please try again later.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (currentAppointmentId) {
        const appointmentData = {
          contact: { fullName, email, notes },
          appointment: { appointmentDate, appointmentTime, PhoneNumber: phoneNumber },
          services: { selectedService },
          vehicleDetails: { make, model, registrationNumber, state, variant, year },
        };

        const appointmentRef = doc(db, "appointments", currentAppointmentId);
        await updateDoc(appointmentRef, appointmentData);
        toast.success("Appointment updated successfully!");

        setCurrentAppointmentId(null);
        resetFormFields();
        fetchAppointments(); // Refresh appointments after update
        setShowModal(false);
      }
    } catch (error) {
      console.error("Error updating appointment: ", error);
      toast.error("Failed to update appointment. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const resetFormFields = () => {
    setFullName("");
    setEmail("");
    setAppointmentDate("");
    setAppointmentTime("");
    setPhoneNumber("");
    setNotes("");
    setSelectedService("");
    setMake("");
    setModel("");
    setRegistrationNumber("");
    setState("");
    setVariant("");
    setYear("");
  };

  // Function to format date for better display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    try {
      // Attempt to format using Date if it's a valid date string
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
      // If not a valid date, return as is
      return dateString;
    } catch (e) {
      return dateString;
    }
  };

  // Extract unique services for filtering
  const serviceOptions = ["All", ...new Set(appointments.map(appointment => 
    appointment.services.selectedService).filter(Boolean))];

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAppointments.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage);

  const renderPaginationItems = () => {
    let items = [];
    let startPage = Math.max(1, currentPage - Math.floor(pageLimit / 2));
    let endPage = startPage + pageLimit - 1;
    
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - pageLimit + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => setCurrentPage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }
    return items;
  };

  // Function to export appointments data
  const handleExportData = () => {
    try {
      // Create CSV content
      let csvContent = "data:text/csv;charset=utf-8,";
      
      // Add headers
      csvContent += "Full Name,Email,Phone Number,Appointment Date,Appointment Time,Service,Make,Model,Registration Number,Year,Notes\n";
      
      // Add data rows
      filteredAppointments.forEach(app => {
        csvContent += [
          app.contact.fullName || "",
          app.contact.email || "",
          app.appointment.PhoneNumber || "",
          app.appointment.appointmentDate || "",
          app.appointment.appointmentTime || "",
          app.services.selectedService || "",
          app.vehicleDetails.make || "",
          app.vehicleDetails.model || "",
          app.vehicleDetails.registrationNumber || "",
          app.vehicleDetails.year || "",
          (app.contact.notes || "").replace(/,/g, ';') // Replace commas in notes to not break CSV
        ].join(",") + "\n";
      });
      
      // Create download link
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "appointments_data.csv");
      document.body.appendChild(link);
      
      // Trigger download and remove link
      link.click();
      document.body.removeChild(link);
      
      toast.success("Appointments data exported successfully");
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.error("Failed to export appointments data");
    }
  };

  return (
    <div className="admin-dashboard d-flex flex-column min-vh-100">
      <ToastContainer position="top-right" autoClose={3000} />
      <AdminHeader />
      
      <div className="d-flex flex-grow-1">
        <Sidebar />
        
        <main className="flex-grow-1 bg-light">
          <Container fluid className="py-4">
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <h5 className="mb-0 font-weight-bold text-primary">
                    <FaCalendarAlt className="me-2" /> Appointments Management
                  </h5>
                  {loading ? (
                    <Spinner animation="border" size="sm" className="ms-3" />
                  ) : (
                    <Badge bg="info" className="ms-2">
                      {filteredAppointments.length}
                    </Badge>
                  )}
                </div>
                
                <div className="d-flex">
                  <InputGroup className="me-3" style={{ width: "250px" }}>
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <FormControl
                      placeholder="Search appointments..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                  
                  <Dropdown className="me-2">
                    <Dropdown.Toggle variant="outline-primary" id="dropdown-actions">
                      Actions
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      <Dropdown.Item onClick={handleExportData}>
                        <FaDownload className="me-2" /> Export Data
                      </Dropdown.Item>
                      <Dropdown.Item onClick={fetchAppointments}>
                        <FaSyncAlt className="me-2" /> Refresh Data
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  
                  <div className="btn-group" role="group">
                    <Button
                      variant={viewMode === "table" ? "primary" : "outline-primary"}
                      onClick={() => setViewMode("table")}
                    >
                      <i className="fas fa-table me-1"></i> Table
                    </Button>
                    <Button
                      variant={viewMode === "cards" ? "primary" : "outline-primary"}
                      onClick={() => setViewMode("cards")}
                    >
                      <i className="fas fa-th me-1"></i> Cards
                    </Button>
                  </div>
                </div>
              </Card.Header>
              
              <div className="px-4 py-3 border-bottom">
                <Row className="align-items-center">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="mb-1 text-muted small">Filter by Service</Form.Label>
                      <Form.Select 
                        value={filterService}
                        onChange={(e) => setFilterService(e.target.value)}
                        size="sm"
                      >
                        {serviceOptions.map((service, idx) => (
                          <option key={idx} value={service}>{service}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  
                  <Col md={9} className="text-md-end mt-3 mt-md-0">
                    <div className="text-muted small">
                      Showing {filteredAppointments.length > 0 ? indexOfFirstItem + 1 : 0} to {Math.min(indexOfLastItem, filteredAppointments.length)} of {filteredAppointments.length} appointments
                    </div>
                  </Col>
                </Row>
              </div>
              
              <Card.Body className="p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Loading appointments data...</p>
                  </div>
                ) : filteredAppointments.length === 0 ? (
                  <div className="text-center py-5 empty-state">
                    <FaExclamationTriangle size={48} className="text-muted mb-3" />
                    <h5>No appointments found</h5>
                    <p className="text-muted">
                      {searchTerm || filterService !== "All" ? 
                        "No appointments match your search criteria." : 
                        "There are no appointments at this time."
                      }
                    </p>
                    {(searchTerm || filterService !== "All") && (
                      <Button variant="outline-primary" onClick={() => {
                        setSearchTerm("");
                        setFilterService("All");
                      }}>
                        Clear Filters
                      </Button>
                    )}
                  </div>
                ) : viewMode === "table" ? (
                  <div className="table-responsive">
                    <Table className="table-hover mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="py-3">
                            <div className="d-flex align-items-center">
                              <FaCalendarAlt className="me-2 text-muted" />
                              Appointment
                            </div>
                          </th>
                          <th className="py-3">
                            <div className="d-flex align-items-center">
                              <FaUserAlt className="me-2 text-muted" />
                              Customer
                            </div>
                          </th>
                          <th className="py-3">
                            <div className="d-flex align-items-center">
                              <FaTools className="me-2 text-muted" />
                              Service
                            </div>
                          </th>
                          <th className="py-3">
                            <div className="d-flex align-items-center">
                              <FaCar className="me-2 text-muted" />
                              Vehicle
                            </div>
                          </th>
                          <th className="py-3 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map((appointment, index) => (
                          <tr key={appointment.id}>
                            <td className="py-3">
                              <div className="d-flex flex-column">
                                <span className="fw-bold">
                                  {formatDate(appointment.appointment.appointmentDate) || "N/A"}
                                </span>
                                <small className="text-muted">
                                  {appointment.appointment.appointmentTime || "No time specified"}
                                </small>
                              </div>
                            </td>
                            <td className="py-3">
                              <div className="d-flex flex-column">
                                <span className="fw-bold">
                                  {appointment.contact.fullName || "N/A"}
                                </span>
                                <div className="d-flex align-items-center">
                                  <FaPhone className="text-muted me-1" size={12} />
                                  <small className="text-muted me-2">
                                    {appointment.appointment.PhoneNumber || "No phone"}
                                  </small>
                                  <FaEnvelope className="text-muted me-1" size={12} />
                                  <small className="text-muted">
                                    {appointment.contact.email || "No email"}
                                  </small>
                                </div>
                              </div>
                            </td>
                            <td className="py-3">
                              <Badge bg="info" className="text-white">
                                {appointment.services.selectedService || "N/A"}
                              </Badge>
                              {appointment.contact.notes && (
                                <div className="mt-1">
                                  <small className="text-muted d-block text-truncate" style={{ maxWidth: "150px" }}>
                                    Note: {appointment.contact.notes}
                                  </small>
                                </div>
                              )}
                            </td>
                            <td className="py-3">
                              <div className="d-flex flex-column">
                                <span className="fw-bold">
                                  {appointment.vehicleDetails.make || "N/A"}
                                  {appointment.vehicleDetails.model && ` ${appointment.vehicleDetails.model}`}
                                  {appointment.vehicleDetails.year && ` (${appointment.vehicleDetails.year})`}
                                </span>
                                <small className="text-muted">
                                  Reg: {appointment.vehicleDetails.registrationNumber || "N/A"}
                                </small>
                              </div>
                            </td>
                            <td className="py-3">
                              <div className="d-flex justify-content-center">
                                <Button 
                                  variant="outline-info" 
                                  size="sm" 
                                  className="me-2"
                                  title="View Details"
                                  onClick={() => handleViewDetails(appointment)}
                                >
                                  <FaEye />
                                </Button>
                                <Button 
                                  variant="outline-primary" 
                                  size="sm" 
                                  className="me-2"
                                  title="Edit Appointment"
                                  onClick={() => handleEdit(appointment.id)}
                                >
                                  <FaEdit />
                                </Button>
                                <Button 
                                  variant="outline-danger" 
                                  size="sm"
                                  title="Delete Appointment"
                                  onClick={() => handleDelete(appointment.id)}
                                >
                                  <FaTrash />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  // Card View
                  <div className="p-3">
                    <Row>
                      {currentItems.map((appointment) => (
                        <Col lg={4} md={6} className="mb-4" key={appointment.id}>
                          <Card className="h-100 shadow-sm hover-shadow">
                            <Card.Header className="bg-light py-2 d-flex justify-content-between align-items-center">
                              <div>
                                <Badge bg="info" className="me-2">
                                  {appointment.services.selectedService || "No Service"}
                                </Badge>
                              </div>
                              <div className="text-end">
                                <small className="text-muted d-block">
                                  <FaCalendarAlt className="me-1" />
                                  {formatDate(appointment.appointment.appointmentDate) || "No date"}
                                </small>
                                <small className="text-muted d-block">
                                  {appointment.appointment.appointmentTime || "No time"}
                                </small>
                              </div>
                            </Card.Header>
                            <Card.Body>
                              <div className="mb-3">
                                <h6 className="mb-1"><FaUserAlt className="me-2 text-primary" /> Customer</h6>
                                <p className="mb-0 fw-bold">{appointment.contact.fullName || "N/A"}</p>
                                <div className="d-flex flex-column mt-1">
                                  <small className="text-muted">
                                    <FaPhone className="me-1" />
                                    {appointment.appointment.PhoneNumber || "No phone"}
                                  </small>
                                  <small className="text-muted">
                                    <FaEnvelope className="me-1" />
                                    {appointment.contact.email || "No email"}
                                  </small>
                                </div>
                              </div>
                              
                              <div className="mb-3">
                                <h6 className="mb-1"><FaCar className="me-2 text-primary" /> Vehicle</h6>
                                <p className="mb-0">
                                  {appointment.vehicleDetails.make || "N/A"}
                                  {appointment.vehicleDetails.model && ` ${appointment.vehicleDetails.model}`}
                                  {appointment.vehicleDetails.year && ` (${appointment.vehicleDetails.year})`}
                                </p>
                                <small className="text-muted">
                                  Reg: {appointment.vehicleDetails.registrationNumber || "N/A"}
                                </small>
                              </div>
                              
                              {appointment.contact.notes && (
                                <div>
                                  <h6 className="mb-1"><FaFileAlt className="me-2 text-primary" /> Notes</h6>
                                  <p className="mb-0 small text-muted">
                                    {appointment.contact.notes.length > 100 ? 
                                      `${appointment.contact.notes.substring(0, 100)}...` : 
                                      appointment.contact.notes
                                    }
                                  </p>
                                </div>
                              )}
                            </Card.Body>
                            <Card.Footer className="bg-white border-0">
                              <div className="d-flex justify-content-between">
                                <Button 
                                  variant="outline-info" 
                                  size="sm"
                                  onClick={() => handleViewDetails(appointment)}
                                >
                                  <FaEye className="me-1" /> Details
                                </Button>
                                <div>
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    className="me-2"
                                    onClick={() => handleEdit(appointment.id)}
                                  >
                                    <FaEdit className="me-1" /> Edit
                                  </Button>
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    onClick={() => handleDelete(appointment.id)}
                                  >
                                    <FaTrash />
                                  </Button>
                                </div>
                              </div>
                            </Card.Footer>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}
              </Card.Body>
              
              {filteredAppointments.length > 0 && (
                <Card.Footer className="bg-white border-0 px-4 py-3">
                  <Row className="align-items-center">
                    <Col className="d-flex justify-content-center justify-content-md-end">
                      {totalPages > 1 && (
                        <Pagination className="mb-0">
                          <Pagination.First
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                          />
                          <Pagination.Prev
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          />
                          {renderPaginationItems()}
                          <Pagination.Next
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          />
                          <Pagination.Last
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                          />
                        </Pagination>
                      )}
                    </Col>
                  </Row>
                </Card.Footer>
              )}
            </Card>
          </Container>
        </main>
      </div>
      
      {/* Edit Appointment Modal */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Appointment</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdate}>
          <Modal.Body className="p-0">
            <Tabs
              id="edit-appointment-tabs"
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="border-bottom mb-3"
            >
              <Tab 
                eventKey="personal" 
                title={<span><FaUserAlt className="me-2" />Customer Details</span>}
              >
                <div className="px-4 py-3">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="editFullName">
                        <Form.Label>Full Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="editEmail">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="editPhoneNumber">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="text"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="editSelectedService">
                        <Form.Label>Selected Service</Form.Label>
                        <Form.Control
                          type="text"
                          value={selectedService}
                          onChange={(e) => setSelectedService(e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3" controlId="editNotes">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </Form.Group>
                </div>
              </Tab>
              
              <Tab 
                eventKey="appointment" 
                title={<span><FaCalendarAlt className="me-2" />Appointment Details</span>}
              >
                <div className="px-4 py-3">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="editAppointmentDate">
                        <Form.Label>Appointment Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={appointmentDate}
                          onChange={(e) => setAppointmentDate(e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="editAppointmentTime">
                        <Form.Label>Appointment Time</Form.Label>
                        <Form.Control
                          type="time"
                          value={appointmentTime}
                          onChange={(e) => setAppointmentTime(e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Tab>
              
              <Tab 
                eventKey="vehicle" 
                title={<span><FaCar className="me-2" />Vehicle Details</span>}
              >
                <div className="px-4 py-3">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="editMake">
                        <Form.Label>Make</Form.Label>
                        <Form.Control
                          type="text"
                          value={make}
                          onChange={(e) => setMake(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="editModel">
                        <Form.Label>Model</Form.Label>
                        <Form.Control
                          type="text"
                          value={model}
                          onChange={(e) => setModel(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="editRegistrationNumber">
                        <Form.Label>Registration Number</Form.Label>
                        <Form.Control
                          type="text"
                          value={registrationNumber}
                          onChange={(e) => setRegistrationNumber(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="editYear">
                        <Form.Label>Year</Form.Label>
                        <Form.Control
                          type="text"
                          value={year}
                          onChange={(e) => setYear(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="editState">
                        <Form.Label>State</Form.Label>
                        <Form.Control
                          type="text"
                          value={state}
                          onChange={(e) => setState(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="editVariant">
                        <Form.Label>Variant</Form.Label>
                        <Form.Control
                          type="text"
                          value={variant}
                          onChange={(e) => setVariant(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Tab>
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <><Spinner size="sm" className="me-2" /> Updating...</> : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* View Details Modal */}
      <Modal 
        show={showDetailsModal} 
        onHide={() => setShowDetailsModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <FaCalendarAlt className="me-2 text-primary" />
            Appointment Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {selectedAppointment && (
            <Tabs defaultActiveKey="personal" className="mb-3">
              <Tab 
                eventKey="personal" 
                title={<span><FaUserAlt className="me-2" />Customer Details</span>}
              >
                <div className="p-4">
                  <Row>
                    <Col md={6}>
                      <dl>
                        <dt className="text-muted">Full Name</dt>
                        <dd className="mb-3 fs-5">{selectedAppointment.contact.fullName || "N/A"}</dd>
                        
                        <dt className="text-muted">Email</dt>
                        <dd className="mb-3">
                          <FaEnvelope className="me-1 text-muted" />
                          {selectedAppointment.contact.email || "N/A"}
                        </dd>
                        
                        <dt className="text-muted">Phone Number</dt>
                        <dd className="mb-3">
                          <FaPhone className="me-1 text-muted" />
                          {selectedAppointment.appointment.PhoneNumber || "N/A"}
                        </dd>
                      </dl>
                    </Col>
                    <Col md={6}>
                      <dl>
                        <dt className="text-muted">Service Type</dt>
                        <dd className="mb-3">
                          <Badge bg="info" className="fs-6">
                            {selectedAppointment.services.selectedService || "N/A"}
                          </Badge>
                        </dd>
                        
                        <dt className="text-muted">Notes</dt>
                        <dd className="mb-3">
                          <div className="p-3 bg-light rounded">
                            {selectedAppointment.contact.notes || "No notes provided"}
                          </div>
                        </dd>
                      </dl>
                    </Col>
                  </Row>
                </div>
              </Tab>
              
              <Tab 
                eventKey="appointment" 
                title={<span><FaCalendarAlt className="me-2" />Appointment Details</span>}
              >
                <div className="p-4">
                  <Row className="mb-4">
                    <Col md={6}>
                      <div className="p-3 border rounded bg-light">
                        <h6 className="text-primary">Date & Time</h6>
                        <div className="d-flex align-items-center mb-2">
                          <div className="bg-primary rounded-circle p-2 text-white me-3">
                            <FaCalendarAlt />
                          </div>
                          <div>
                            <div className="fs-4">{formatDate(selectedAppointment.appointment.appointmentDate) || "No date"}</div>
                            <div className="text-muted">{selectedAppointment.appointment.appointmentTime || "No time specified"}</div>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="p-3 border rounded">
                        <h6 className="text-primary">Service Information</h6>
                        <div className="mb-2">
                          <div className="mb-2">
                            <strong>Selected Service:</strong>
                            <div className="mt-1">
                              <Badge bg="info" className="p-2 fs-6">
                                {selectedAppointment.services.selectedService || "N/A"}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Tab>
              
              <Tab 
                eventKey="vehicle" 
                title={<span><FaCar className="me-2" />Vehicle Details</span>}
              >
                <div className="p-4">
                  <div className="p-3 border rounded mb-4">
                    <h6 className="text-primary mb-3">Vehicle Information</h6>
                    <Row>
                      <Col md={6}>
                        <dl className="mb-0">
                          <dt className="text-muted">Make & Model</dt>
                          <dd className="mb-3 fs-5">
                            {selectedAppointment.vehicleDetails.make || "N/A"}
                            {selectedAppointment.vehicleDetails.model && ` ${selectedAppointment.vehicleDetails.model}`}
                          </dd>
                          
                          <dt className="text-muted">Year</dt>
                          <dd className="mb-3">{selectedAppointment.vehicleDetails.year || "N/A"}</dd>
                          
                          <dt className="text-muted">Variant</dt>
                          <dd className="mb-0">{selectedAppointment.vehicleDetails.variant || "N/A"}</dd>
                        </dl>
                      </Col>
                      <Col md={6}>
                        <dl className="mb-0">
                          <dt className="text-muted">Registration Number</dt>
                          <dd className="mb-3 fs-5">
                            {selectedAppointment.vehicleDetails.registrationNumber || "N/A"}
                          </dd>
                          
                          <dt className="text-muted">State</dt>
                          <dd className="mb-3">{selectedAppointment.vehicleDetails.state || "N/A"}</dd>
                        </dl>
                      </Col>
                    </Row>
                  </div>
                </div>
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
          {selectedAppointment && (
            <Button 
              variant="primary" 
              onClick={() => {
                setShowDetailsModal(false);
                handleEdit(selectedAppointment.id);
              }}
            >
              <FaEdit className="me-2" />
              Edit Appointment
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AppointmentsData;